import React, { useState, useRef } from 'react';
import { database } from '../firebase';
import { ref, set, get } from 'firebase/database';
import {
  Upload,
  X,
  Brain,
  User,
  Folder,
  FolderOpen,
  CheckCircle,
  AlertCircle,
  Loader,
  FileJson,
  Sparkles,
  Building2,
  SortAsc
} from 'lucide-react';
import './ChatGPTImport.css';

const ChatGPTImport = ({ userId, projects, onClose, onImportComplete }) => {
  const [_file, setFile] = useState(null); // eslint-disable-line no-unused-vars
  const [parsing, setParsing] = useState(false);
  const [sorting, setSorting] = useState(false);
  const [parsedConversations, setParsedConversations] = useState([]);
  const [sortedData, setSortedData] = useState({
    personal: [],
    entities: {} // { projectId: [conversations] }
  });
  const [importStatus, setImportStatus] = useState(null); // 'success', 'error'
  const [step, setStep] = useState(1); // 1: upload, 2: review, 3: confirm
  const [parseError, setParseError] = useState(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // Parse the ChatGPT export file
  const parseFile = async (fileContent) => {
    setParsing(true);
    try {
      const data = JSON.parse(fileContent);

      // ChatGPT export format has conversations array
      const conversations = data.map(conv => ({
        id: conv.id,
        title: conv.title || 'Untitled',
        createTime: conv.create_time,
        updateTime: conv.update_time,
        messages: extractMessages(conv.mapping),
        keywords: extractKeywords(conv),
        summary: generateSummary(conv)
      }));

      setParsedConversations(conversations);
      setStep(2);

      // Auto-sort after parsing
      await sortConversations(conversations);
    } catch (e) {
      console.error('Failed to parse ChatGPT export:', e);
      setImportStatus('error');
    } finally {
      setParsing(false);
    }
  };

  // Extract messages from ChatGPT mapping format
  const extractMessages = (mapping) => {
    if (!mapping) return [];

    const messages = [];
    Object.values(mapping).forEach(node => {
      if (node.message && node.message.content && node.message.content.parts) {
        messages.push({
          role: node.message.author?.role || 'unknown',
          content: node.message.content.parts.join(' '),
          timestamp: node.message.create_time
        });
      }
    });

    return messages.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
  };

  // Extract keywords from conversation
  const extractKeywords = (conv) => {
    const text = conv.title?.toLowerCase() || '';
    const allText = Object.values(conv.mapping || {})
      .map(n => n.message?.content?.parts?.join(' ') || '')
      .join(' ')
      .toLowerCase();

    // Extract potential keywords
    const keywords = new Set();

    // Add title words
    text.split(/\s+/).forEach(word => {
      if (word.length > 3) keywords.add(word);
    });

    // Look for common project-related terms
    const projectTerms = ['project', 'app', 'website', 'feature', 'build', 'create', 'design', 'develop'];
    projectTerms.forEach(term => {
      if (allText.includes(term)) keywords.add(term);
    });

    return Array.from(keywords);
  };

  // Generate a summary of the conversation
  const generateSummary = (conv) => {
    const messages = extractMessages(conv.mapping);
    if (messages.length === 0) return 'Empty conversation';

    // Get first user message as summary
    const firstUser = messages.find(m => m.role === 'user');
    if (firstUser) {
      return firstUser.content.slice(0, 200) + (firstUser.content.length > 200 ? '...' : '');
    }

    return conv.title || 'No summary available';
  };

  // AI sorting logic - match conversations to projects or personal
  const sortConversations = async (conversations) => {
    setSorting(true);

    const sorted = {
      personal: [],
      entities: {}
    };

    // Initialize entity buckets for each project
    projects.forEach(project => {
      sorted.entities[project.id] = [];
    });

    conversations.forEach(conv => {
      const convText = `${conv.title} ${conv.summary}`.toLowerCase();
      const keywords = conv.keywords;

      let bestMatch = null;
      let bestScore = 0;

      // Score each project
      projects.forEach(project => {
        let score = 0;
        const projectName = project.name?.toLowerCase() || '';
        const projectGoals = (project.goals || []).map(g => g.title?.toLowerCase() || '').join(' ');
        const projectTodos = (project.todos || []).map(t => t.task?.toLowerCase() || '').join(' ');
        const projectContext = `${projectName} ${projectGoals} ${projectTodos}`;

        // Check for project name match
        if (convText.includes(projectName) || projectName.split(' ').some(word =>
          word.length > 3 && convText.includes(word)
        )) {
          score += 10;
        }

        // Check keyword overlap
        keywords.forEach(keyword => {
          if (projectContext.includes(keyword)) score += 2;
        });

        // Check if conversation mentions project goals/tasks
        (project.goals || []).forEach(goal => {
          if (convText.includes(goal.title?.toLowerCase() || '')) score += 5;
        });

        if (score > bestScore) {
          bestScore = score;
          bestMatch = project.id;
        }
      });

      // Determine where to place the conversation
      if (bestScore >= 5 && bestMatch) {
        // Strong match to a project
        sorted.entities[bestMatch].push({
          ...conv,
          matchScore: bestScore,
          matchType: 'entity'
        });
      } else {
        // Personal profile (no strong project match)
        sorted.personal.push({
          ...conv,
          matchScore: bestScore,
          matchType: 'personal'
        });
      }
    });

    setSortedData(sorted);
    setSorting(false);
    setStep(2);
  };

  // Handle file selection (single JSON file)
  const handleFileSelect = (e) => {
    setParseError(null);
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.name.endsWith('.json')) {
      setFile(selectedFile);

      const reader = new FileReader();
      reader.onload = (event) => {
        parseFile(event.target.result);
      };
      reader.readAsText(selectedFile);
    }
  };

  // Handle folder selection
  const handleFolderSelect = (e) => {
    setParseError(null);
    const files = Array.from(e.target.files || []);

    // Find conversations.json in the folder
    const conversationsFile = files.find(f =>
      f.name === 'conversations.json' ||
      f.webkitRelativePath?.endsWith('/conversations.json')
    );

    if (conversationsFile) {
      setFile(conversationsFile);
      const reader = new FileReader();
      reader.onload = (event) => {
        parseFile(event.target.result);
      };
      reader.readAsText(conversationsFile);
    } else {
      setParseError('No conversations.json found in folder. Make sure you selected the ChatGPT export folder.');
    }
  };

  // Move conversation between buckets
  const moveConversation = (convId, fromBucket, toBucket) => {
    setSortedData(prev => {
      const newData = { ...prev };
      let conv;

      // Find and remove from source
      if (fromBucket === 'personal') {
        const idx = newData.personal.findIndex(c => c.id === convId);
        if (idx !== -1) {
          conv = newData.personal.splice(idx, 1)[0];
        }
      } else {
        const idx = newData.entities[fromBucket]?.findIndex(c => c.id === convId);
        if (idx !== -1) {
          conv = newData.entities[fromBucket].splice(idx, 1)[0];
        }
      }

      // Add to destination
      if (conv) {
        if (toBucket === 'personal') {
          newData.personal.push({ ...conv, matchType: 'personal' });
        } else {
          if (!newData.entities[toBucket]) {
            newData.entities[toBucket] = [];
          }
          newData.entities[toBucket].push({ ...conv, matchType: 'entity' });
        }
      }

      return newData;
    });
  };

  // Import sorted data to Firebase
  const importToFirebase = async () => {
    setParsing(true);
    try {
      // Save personal profile bank
      const personalBankRef = ref(database, `users/${userId}/personalProfileBank`);
      const existingPersonal = (await get(personalBankRef)).val() || [];

      const newPersonalEntries = sortedData.personal.map(conv => ({
        type: 'CHATGPT_IMPORT',
        source: 'chatgpt',
        title: conv.title,
        summary: conv.summary,
        keywords: conv.keywords,
        messageCount: conv.messages.length,
        importedAt: Date.now(),
        originalCreateTime: conv.createTime,
        // Store key insights from messages
        insights: extractInsights(conv.messages)
      }));

      await set(personalBankRef, [...existingPersonal, ...newPersonalEntries]);

      // Save to each entity's data bank
      for (const [projectId, conversations] of Object.entries(sortedData.entities)) {
        if (conversations.length === 0) continue;

        const entityBankRef = ref(database, `users/${userId}/projects/${projectId}/dataBank`);
        const existingBank = (await get(entityBankRef)).val() || [];

        const newEntries = conversations.map(conv => ({
          type: 'CHATGPT_IMPORT',
          source: 'chatgpt',
          data: {
            title: conv.title,
            summary: conv.summary,
            keywords: conv.keywords,
            messageCount: conv.messages.length
          },
          timestamp: Date.now(),
          aiContext: {
            action: 'CHATGPT_IMPORT',
            item: conv.title,
            insights: extractInsights(conv.messages)
          }
        }));

        await set(entityBankRef, [...existingBank, ...newEntries]);
      }

      setImportStatus('success');
      setStep(3);

      if (onImportComplete) {
        onImportComplete({
          personalCount: sortedData.personal.length,
          entityCounts: Object.fromEntries(
            Object.entries(sortedData.entities).map(([k, v]) => [k, v.length])
          )
        });
      }
    } catch (e) {
      console.error('Failed to import to Firebase:', e);
      setImportStatus('error');
    } finally {
      setParsing(false);
    }
  };

  // Extract key insights from messages
  const extractInsights = (messages) => {
    const insights = [];

    // Look for decisions, preferences, goals mentioned
    messages.forEach(msg => {
      if (msg.role === 'user') {
        const content = msg.content.toLowerCase();

        // Detect preferences
        if (content.includes('i prefer') || content.includes('i want') || content.includes('i like')) {
          insights.push({
            type: 'preference',
            text: msg.content.slice(0, 150)
          });
        }

        // Detect goals
        if (content.includes('my goal') || content.includes('i need to') || content.includes('trying to')) {
          insights.push({
            type: 'goal',
            text: msg.content.slice(0, 150)
          });
        }

        // Detect decisions
        if (content.includes('i decided') || content.includes('going to') || content.includes('will do')) {
          insights.push({
            type: 'decision',
            text: msg.content.slice(0, 150)
          });
        }
      }
    });

    return insights.slice(0, 5); // Limit to 5 insights per conversation
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project?.name || projectId;
  };

  return (
    <div className="chatgpt-import-overlay" onClick={onClose}>
      <div className="chatgpt-import-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="import-header">
          <div className="import-title">
            <Brain size={24} />
            <div>
              <h2>IMPORT_CHATGPT_DATA</h2>
              <span className="import-subtitle">Import conversations to Personal & Entity Banks</span>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="import-steps">
          <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'complete' : ''}`}>
            <div className="step-number">1</div>
            <span>Upload</span>
          </div>
          <div className="step-line" />
          <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'complete' : ''}`}>
            <div className="step-number">2</div>
            <span>Review & Sort</span>
          </div>
          <div className="step-line" />
          <div className={`step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <span>Complete</span>
          </div>
        </div>

        {/* Content */}
        <div className="import-content">
          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="upload-section">
              {/* Hidden file inputs */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".json"
                style={{ display: 'none' }}
              />
              <input
                type="file"
                ref={folderInputRef}
                onChange={handleFolderSelect}
                webkitdirectory=""
                directory=""
                style={{ display: 'none' }}
              />

              {parsing ? (
                <div className="upload-zone parsing">
                  <Loader size={48} className="spin" />
                  <h3>PARSING_DATA...</h3>
                  <p>Analyzing conversation structure</p>
                </div>
              ) : (
                <div className="upload-options">
                  {/* Folder Upload - Primary */}
                  <div
                    className="upload-zone folder-zone"
                    onClick={() => folderInputRef.current?.click()}
                  >
                    <FolderOpen size={48} />
                    <h3>SELECT_EXPORT_FOLDER</h3>
                    <p>Choose the unzipped ChatGPT export folder</p>
                    <div className="upload-hint recommended">RECOMMENDED</div>
                  </div>

                  <div className="upload-divider">
                    <span>OR</span>
                  </div>

                  {/* Single File Upload */}
                  <div
                    className="upload-zone file-zone"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FileJson size={32} />
                    <h3>SELECT_CONVERSATIONS.JSON</h3>
                    <p>Select the file directly</p>
                  </div>
                </div>
              )}

              {parseError && (
                <div className="parse-error">
                  <AlertCircle size={16} />
                  <span>{parseError}</span>
                </div>
              )}

              <div className="upload-instructions">
                <h4>How to export from ChatGPT:</h4>
                <ol>
                  <li>Go to ChatGPT → Settings → Data Controls</li>
                  <li>Click "Export Data" and wait for email</li>
                  <li>Download and unzip the export</li>
                  <li>Select the folder above</li>
                </ol>
              </div>

              <div className="bank-preview">
                <div className="bank-card personal">
                  <User size={20} />
                  <div>
                    <h4>Personal Profile Bank</h4>
                    <p>Your preferences, goals, decisions</p>
                  </div>
                </div>
                <div className="bank-card entity">
                  <Building2 size={20} />
                  <div>
                    <h4>Entity Banks</h4>
                    <p>{projects.length} projects available</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Review & Sort */}
          {step === 2 && (
            <div className="review-section">
              {sorting ? (
                <div className="sorting-indicator">
                  <Sparkles size={32} className="sparkle" />
                  <h3>AI_SORTING...</h3>
                  <p>Matching conversations to entities</p>
                </div>
              ) : (
                <>
                  <div className="sort-summary">
                    <SortAsc size={18} />
                    <span>
                      {parsedConversations.length} conversations sorted •
                      {sortedData.personal.length} personal •
                      {Object.values(sortedData.entities).reduce((a, b) => a + b.length, 0)} entity-matched
                    </span>
                  </div>

                  <div className="banks-container">
                    {/* Personal Bank */}
                    <div className="bank-section">
                      <div className="bank-header personal">
                        <User size={18} />
                        <span>PERSONAL_PROFILE</span>
                        <span className="count">{sortedData.personal.length}</span>
                      </div>
                      <div className="bank-items">
                        {sortedData.personal.slice(0, 5).map(conv => (
                          <div key={conv.id} className="conv-item">
                            <div className="conv-title">{conv.title}</div>
                            <div className="conv-meta">
                              {conv.messages.length} messages
                            </div>
                            <select
                              className="move-select"
                              onChange={(e) => {
                                if (e.target.value) {
                                  moveConversation(conv.id, 'personal', e.target.value);
                                  e.target.value = '';
                                }
                              }}
                              defaultValue=""
                            >
                              <option value="">Move to...</option>
                              {projects.map(p => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                        {sortedData.personal.length > 5 && (
                          <div className="more-indicator">
                            +{sortedData.personal.length - 5} more
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Entity Banks */}
                    {projects.map(project => {
                      const convs = sortedData.entities[project.id] || [];
                      if (convs.length === 0) return null;

                      return (
                        <div key={project.id} className="bank-section">
                          <div className="bank-header entity">
                            <Folder size={18} />
                            <span>{project.name}</span>
                            <span className="count">{convs.length}</span>
                          </div>
                          <div className="bank-items">
                            {convs.slice(0, 3).map(conv => (
                              <div key={conv.id} className="conv-item">
                                <div className="conv-title">{conv.title}</div>
                                <div className="conv-meta">
                                  Score: {conv.matchScore}
                                </div>
                                <select
                                  className="move-select"
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      moveConversation(conv.id, project.id, e.target.value);
                                      e.target.value = '';
                                    }
                                  }}
                                  defaultValue=""
                                >
                                  <option value="">Move to...</option>
                                  <option value="personal">Personal</option>
                                  {projects.filter(p => p.id !== project.id).map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                  ))}
                                </select>
                              </div>
                            ))}
                            {convs.length > 3 && (
                              <div className="more-indicator">
                                +{convs.length - 3} more
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="import-btn-container">
                    <button
                      className="import-btn"
                      onClick={importToFirebase}
                      disabled={parsing}
                    >
                      {parsing ? (
                        <>
                          <Loader size={18} className="spin" />
                          <span>IMPORTING...</span>
                        </>
                      ) : (
                        <>
                          <Upload size={18} />
                          <span>IMPORT_TO_BANKS</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3: Complete */}
          {step === 3 && (
            <div className="complete-section">
              {importStatus === 'success' ? (
                <>
                  <CheckCircle size={64} className="success-icon" />
                  <h3>IMPORT_COMPLETE</h3>
                  <p>Your ChatGPT data has been sorted into banks</p>

                  <div className="import-stats">
                    <div className="stat">
                      <User size={20} />
                      <div>
                        <span className="stat-value">{sortedData.personal.length}</span>
                        <span className="stat-label">Personal</span>
                      </div>
                    </div>
                    {Object.entries(sortedData.entities).map(([id, convs]) => {
                      if (convs.length === 0) return null;
                      return (
                        <div key={id} className="stat">
                          <Folder size={20} />
                          <div>
                            <span className="stat-value">{convs.length}</span>
                            <span className="stat-label">{getProjectName(id)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button className="done-btn" onClick={onClose}>
                    DONE
                  </button>
                </>
              ) : (
                <>
                  <AlertCircle size={64} className="error-icon" />
                  <h3>IMPORT_FAILED</h3>
                  <p>Something went wrong. Please try again.</p>
                  <button className="retry-btn" onClick={() => setStep(1)}>
                    RETRY
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatGPTImport;
